import type {
  ApiResponse,
  Order,
  OrderStatsEntry,
  OrderStatus,
  PaginatedResponse,
  RemovedOrdersStats,
} from '@/types/api';

import { api } from './api';

interface OrderInput {
  number?: string;
  clientId: number;
  description?: string;
  details?: string;
  deliveryDate?: string;
  freightValue?: number;
  discountValue?: number;
  status?: OrderStatus;
  items: Array<{ productId: number; qty: number; unitPrice?: number }>;
}

interface BulkUpdatePayload {
  orderIds: number[];
  clientId?: number;
  status?: OrderStatus;
  deliveryDate?: string | null;
  description?: string | null;
  freightValue?: number;
}

const extractFilename = (contentDisposition?: string) => {
  if (!contentDisposition) {
    return null;
  }
  const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(contentDisposition);
  if (match) {
    return decodeURIComponent(match[1] ?? match[2]);
  }
  return null;
};

export const orderService = {
  list: async (params?: Record<string, unknown> & { userId?: number; includeInactive?: boolean }) => {
    const queryParams: Record<string, unknown> = { ...params };
    if (queryParams.userId !== undefined) {
      queryParams.filterUserId = queryParams.userId;
      delete queryParams.userId;
    }
    if (queryParams.includeInactive !== undefined) {
      queryParams.includeInactive = queryParams.includeInactive ? 'true' : 'false';
    }
    const response = await api.get<PaginatedResponse<Order>>('/orders', { params: queryParams });
    return response.data;
  },
  get: async (id: number) => {
    const response = await api.get<ApiResponse<Order>>(`/orders/${id}`);
    return response.data.data;
  },
  create: async (payload: OrderInput) => {
    const response = await api.post<ApiResponse<Order>>('/orders', payload);
    return response.data.data;
  },
  update: async (id: number, payload: Partial<OrderInput>) => {
    const response = await api.put<ApiResponse<Order>>(`/orders/${id}`, payload);
    return response.data.data;
  },
  updateStatus: async (id: number, status: OrderStatus) => {
    const response = await api.patch<ApiResponse<Order>>(`/orders/${id}/status`, { status });
    return response.data.data;
  },
  bulkUpdate: async (payload: BulkUpdatePayload) => {
    const response = await api.patch<ApiResponse<Order[]>>('/orders/bulk-update', payload);
    return response.data.data;
  },
  remove: async (id: number) => {
    await api.delete(`/orders/${id}`);
  },
  downloadContract: async (id: number) => {
    const response = await api.get(`/orders/${id}/contract`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },
  downloadReviewPdf: async (id: number) => {
    const response = await api.get(`/orders/${id}/review-pdf`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },
  report: async (params?: Record<string, unknown>) => {
    const response = await api.get<
      ApiResponse<{ totalOrders: number; totalValue: number; orders: Order[] }>
    >('/orders/report', { params });
    return response.data.data;
  },
  exportReportCsv: async (params?: Record<string, unknown>) => {
    const response = await api.get<Blob>('/orders/report', {
      params: { ...params, format: 'csv' },
      responseType: 'blob',
    });

    const filename =
      extractFilename(response.headers['content-disposition']) ?? 'pedidoo-relatorio.csv';
    return { blob: response.data, filename };
  },
  getRemovedOrdersStats: async (params?: Record<string, unknown>) => {
    const response = await api.get<ApiResponse<RemovedOrdersStats>>('/orders/removed-stats', {
      params,
    });
    return response.data.data;
  },

  exportReportPdf: async (params?: Record<string, unknown>) => {
    const response = await api.get<Blob>('/orders/report', {
      params: { ...params, format: 'pdf' },
      responseType: 'blob',
    });

    const filename =
      extractFilename(response.headers['content-disposition']) ?? 'pedidoo-relatorio.pdf';
    return { blob: response.data, filename };
  },
};
